import { faPlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Flex, Stack, useMantineTheme } from '@mantine/core';
import type { JSX } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge, Button, CardV2 as Card, Skeleton, Toggle } from '@feathr/components';
import { useLocalUrl } from '@feathr/extender/state';

import Page from '../Page';
import ConversionsFiltersPopover from './ConversionsFiltersPopover';
import {
  activeFilterCount,
  type IFiltersState,
  isCampaignTypeFilter,
} from './ConversionsFiltersPopover/ConversionsFiltersPopover.utils';
import KpiTiles from './KpiTiles';
import { useDashboardUrlState } from './ReportsConversionsDashboardPage.hooks';
import TimeframeControls from './TimeframeControls';

function ReportsConversionsDashboardPage(): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const localUrl = useLocalUrl();
  const { state, range, setPreset, setCustomRange, setFilters, setArchived } =
    useDashboardUrlState();

  const appliedFilters = useMemo<IFiltersState>(
    () => ({
      attr: state.attr,
      projects: state.projects,
      types: state.types.filter(isCampaignTypeFilter),
    }),
    [state.attr, state.projects, state.types],
  );

  const filterCount = activeFilterCount(appliedFilters);

  function handleApplyFilters(applied: IFiltersState): void {
    setFilters({
      attr: applied.attr,
      projects: [...applied.projects],
      types: [...applied.types],
    });
  }

  function handleArchivedChange(value?: boolean): void {
    setArchived(Boolean(value));
  }

  function renderFiltersTrigger(toggle: () => void): JSX.Element {
    return (
      <Button
        name={'open-filters'}
        onClick={toggle}
        suffix={filterCount > 0 ? <Badge value={filterCount} /> : undefined}
        type={'secondary'}
      >
        {t('Filters')}
      </Button>
    );
  }

  return (
    <Page
      actions={
        <Button
          href={localUrl('/reports/conversions/goal-manager')}
          name={'add-goal'}
          prefix={<FontAwesomeIcon icon={faPlus} />}
          type={'primary'}
        >
          {t('Add goal')}
        </Button>
      }
      pageTitle={t('Conversions Dashboard')}
      title={t('Dashboard')}
    >
      <Stack gap={theme.spacing['4']}>
        <Flex align={'flex-end'} gap={theme.spacing['3']} justify={'space-between'}>
          <TimeframeControls
            onPresetChange={setPreset}
            onRangeChange={setCustomRange}
            preset={state.timeframe}
            range={range}
          />
          <Flex align={'center'} gap={theme.spacing['3']}>
            <Toggle
              label={t('Show archived')}
              onChange={handleArchivedChange}
              value={state.archived}
            />
            <ConversionsFiltersPopover
              appliedFilters={appliedFilters}
              onApply={handleApplyFilters}
              trigger={renderFiltersTrigger}
            />
          </Flex>
        </Flex>

        <Card width={'full'}>
          <Card.Content>
            <KpiTiles range={range} state={state} />
          </Card.Content>
        </Card>
        <Card width={'full'}>
          <Card.Content>
            <Skeleton height={320} />
          </Card.Content>
        </Card>
        <Card width={'full'}>
          <Card.Content>
            <Skeleton height={480} />
          </Card.Content>
        </Card>
      </Stack>
    </Page>
  );
}

export default ReportsConversionsDashboardPage;
